import { useEffect, useState } from 'react';
import { useParams, useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getPlayerById } from '../../../features/playerProfile/playerProfileSlice';
import { followPlayer, unfollowPlayer, getScoutDashboard } from '../../../features/scout/scoutProfileSlice';
import { normalizePlayerClass } from '../../../utils/normalizePlayerClass';
import { PlayerInfoView } from './components/PlayerInfoView';
import { VideoSectionView } from './components/VideoSectionView';
import { PlayerBioView } from './components/PlayerBioView';
import { StatisticsSectionView } from './components/StatisticsSectionView';
import { AwardsSectionView } from './components/AwardsSectionView';
import { AcademicInfoView } from './components/AcademicInfoView';
import { CoachRecommendationView } from './components/CoachRecommendationView';
import { ScoutSidebar } from '../dashboard/ScoutSidebar';

export const PlayerProfileView = () => {
  const { playerId } = useParams();
  const { activeView } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { viewedPlayer, viewedPlayerLoading, viewedPlayerError } = useSelector((state) => state.playerProfile);
  const { followedPlayers } = useSelector((state) => state.scoutProfile);

  // Check if player data passed via navigation state
  const [playerFromState] = useState(location.state?.playerData || null);
  const [followLoading, setFollowLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);

  // Fetch player data only if not available in state
  useEffect(() => {
    if (!playerFromState && playerId) {
      dispatch(getPlayerById(playerId));
    }
  }, [dispatch, playerId, playerFromState]);

  // Fetch followed players on mount
  useEffect(() => {
    dispatch(getScoutDashboard({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Use data from state if available, otherwise use fetched data
  const currentPlayer = playerFromState || viewedPlayer;

  // Check if current player is in followed players list
  useEffect(() => {
    const currentPlayerId = currentPlayer?._id || playerId;
    if (currentPlayerId && followedPlayers && followedPlayers.length > 0) {
      const isPlayerFollowed = followedPlayers.some(
        (player) => player._id === currentPlayerId || player.id === currentPlayerId
      );
      setIsFollowing(isPlayerFollowed);
    } else {
      setIsFollowing(false);
    }
  }, [currentPlayer, playerId, followedPlayers]);

  const handleFollowToggle = async () => {
    const currentPlayerId = currentPlayer?._id || playerId;
    if (!currentPlayerId || followLoading) return;

    setFollowLoading(true);
    try {
      if (isFollowing) {
        await dispatch(unfollowPlayer(currentPlayerId)).unwrap();
        setIsFollowing(false);
      } else {
        await dispatch(followPlayer(currentPlayerId)).unwrap();
        setIsFollowing(true);
      }
      // Refresh the followed players list to keep it in sync
      dispatch(getScoutDashboard({ page: 1, limit: 100 }));
    } catch (error) {
      console.error('Failed to toggle player follow:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  // Handle stats from roster API (stats.battingStats) vs profile API (battingStats)
  // Also handle both array and object formats
  const battingStats = currentPlayer?.stats?.battingStats
    ? [currentPlayer.stats.battingStats]
    : (currentPlayer?.battingStats
      ? (Array.isArray(currentPlayer.battingStats) ? currentPlayer.battingStats : [currentPlayer.battingStats])
      : []);

  const pitchingStats = currentPlayer?.stats?.pitchingStats
    ? [currentPlayer.stats.pitchingStats]
    : (currentPlayer?.pitchingStats
      ? (Array.isArray(currentPlayer.pitchingStats) ? currentPlayer.pitchingStats : [currentPlayer.pitchingStats])
      : []);

  const fieldingStats = currentPlayer?.stats?.fieldingStats
    ? [currentPlayer.stats.fieldingStats]
    : (currentPlayer?.fieldingStats
      ? (Array.isArray(currentPlayer.fieldingStats) ? currentPlayer.fieldingStats : [currentPlayer.fieldingStats])
      : []);

  // Map player data to expected format
  const playerData = currentPlayer ? {
    id: currentPlayer._id,
    name: `${currentPlayer.firstName || ''} ${currentPlayer.lastName || ''}`.trim() || currentPlayer.name || '-',
    photo: currentPlayer.profileImage || null,
    dateOfBirth: currentPlayer.dob || currentPlayer.dateOfBirth || '-',
    class: normalizePlayerClass(currentPlayer.playerClass || currentPlayer.class),
    bt: currentPlayer.batsThrows || currentPlayer.batThrow || '-',
    position: currentPlayer.primaryPosition || currentPlayer.position || '-',
    hometown: currentPlayer.hometown || '-',
    commitment: currentPlayer.commitmentStatus || '-',
    highSchool: currentPlayer.highSchool || '-',
    college: currentPlayer.previousSchool || '-',
    height: currentPlayer.height || '-',
    weight: currentPlayer.weight ? (typeof currentPlayer.weight === 'number' ? `${currentPlayer.weight} LBS` : currentPlayer.weight) : '-',
    eligibilityYears: [],
    bio: currentPlayer.playerBio || currentPlayer.description || currentPlayer.bio,
    gpa: currentPlayer.academic_info_gpa || '-',
    sat: currentPlayer.academic_info_sat || '-',
    act: currentPlayer.academic_info_act || '-',
    classRank: '-',
    jerseyNumber: currentPlayer.jerseyNumber || null,
    committedTo: currentPlayer.committedTo || '-',
    WHIP: currentPlayer.WHIP || '-',
    // Include team data
    team: currentPlayer.team || null,
    // Profile status and contact info
    registrationStatus: currentPlayer.registrationStatus || null,
    phoneNumber: currentPlayer.phoneNumber || null,
    email: currentPlayer.email || null,
    // Social media links
    instaURL: currentPlayer.instaURL || null,
    xURL: currentPlayer.xURL || null,
  } : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Left Sidebar */}
      <div className="lg:col-span-1 ">
        <ScoutSidebar activeView={activeView} />
      </div>

      {/* Right Content Area */}
      <main className="lg:col-span-3 flex-1 p-6 overflow-y-auto">
        {/* Back Button and Follow Button */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-300 hover:text-white text-sm font-medium transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
            <span>Back</span>
          </button>

          {/* Right side buttons */}
          <div className="flex items-center gap-3">
            {/* Message Player Button */}
            <button
              onClick={() => {
                const rolePrefix = location.pathname.split('/')[1] || 'scout';
                navigate(`/${rolePrefix}/messages`, {
                  state: {
                    newConversationPlayer: {
                      _id: currentPlayer?._id,
                      firstName: currentPlayer?.firstName || '',
                      lastName: currentPlayer?.lastName || '',
                      profileImage: currentPlayer?.profileImage || null,
                    },
                  },
                });
              }}
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>Message</span>
            </button>

            {/* Follow/Unfollow Button */}
            <button
              onClick={handleFollowToggle}
              disabled={followLoading}
              className={`${
                isFollowing
                  ? 'bg-gray-600 hover:bg-gray-700'
                  : 'bg-cyan-500 hover:bg-cyan-600'
              } text-white px-6 py-2.5 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
            >
              {followLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>{isFollowing ? 'Unfollowing...' : 'Following...'}</span>
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {isFollowing ? (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    )}
                  </svg>
                  <span>{isFollowing ? 'Unfollow Player' : 'Follow Player'}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Loading State - only show when fetching from API */}
        {!playerFromState && viewedPlayerLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Error State */}
        {!playerFromState && viewedPlayerError && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
            {viewedPlayerError}
          </div>
        )}

        {/* Profile Content */}
        {playerData && (
          <div className="max-w-7xl mx-auto border border-slate-700/50 px-6 rounded-2xl space-y-6 relative z-20">
            {/* Player Info Fields */}
            <PlayerInfoView playerData={playerData} />

            {/* Video Section */}
            <VideoSectionView
              videos={currentPlayer?.videos || []}
              profile={currentPlayer}
              playerId={currentPlayer?._id || playerId}
            />

            {/* Player Bio */}
            <PlayerBioView bio={playerData.bio} />

            {/* Statistics - Full Width */}
            <div className="pt-12">
              <StatisticsSectionView
                battingStats={battingStats}
                pitchingStats={pitchingStats}
                fieldingStats={fieldingStats}
                conferenceData={currentPlayer?.conferenceData || null}
                summerData={currentPlayer?.summerData || null}
                position={playerData.position}
                teamName={playerData.team && typeof playerData.team === 'object' ? playerData.team.name : (typeof playerData.team === 'string' ? playerData.team : null)}
                teamLeague={playerData.team && typeof playerData.team === 'object' ? playerData.team.league : null}
              />
            </div>

            {/* Previous Teams | Awards | Coach Recommendation - 3 column row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
              {/* Previous Teams */}
              <div className="border border-slate-700/50 rounded-2xl p-5 flex flex-col">
                <h3 className="text-white font-bold text-lg mb-4">PREVIOUS TEAMS</h3>
                <div className="flex flex-wrap gap-3 border border-slate-700/50 rounded-2xl p-2 min-h-[48px]">
                  {currentPlayer?.previousTeams ? (
                    String(currentPlayer.previousTeams).split(',').map((t) => t.trim()).filter(Boolean).map((team, i) => (
                      <span key={i} className="px-4 py-2 rounded-full bg-violet-500/20 border border-violet-500/40 text-violet-400 text-sm font-medium">
                        {team}
                      </span>
                    ))
                  ) : (
                    <p className="text-slate-500 text-sm px-2 py-1">No previous teams</p>
                  )}
                </div>
              </div>

              {/* Awards */}
              <div className="border border-slate-700/50 rounded-2xl p-5 flex flex-col">
                <AwardsSectionView awards={currentPlayer?.awardsAchievements || []} />
              </div>

              {/* Coach Recommendation */}
              <div className="border border-slate-700/50 rounded-2xl p-5 flex flex-col">
                <CoachRecommendationView
                  recommendation={{
                    ...currentPlayer?.coachRecommendation,
                    position: playerData?.position
                  }}
                />
              </div>
            </div>

            {/* Academic Info */}
            <AcademicInfoView
              academicData={playerData}
              academicDocument={currentPlayer?.acedemicInfo || currentPlayer?.academicInfo}
            />
          </div>
        )}
      </main>
    </div>
  );
};

export default PlayerProfileView;
